import type { QueryRequestData } from '@ricoh/ria.client.core/plugins/swr/request';
import type { DataSource } from '@ricoh/ria.client.core/types/component/data.type';

import { getData, getPageableData } from '@ricoh/ria.client.core/plugins/swr/common';
import { createFetcher } from '@ricoh/ria.client.core/plugins/swr/fetcher';
import { flatItemMapper } from '@ricoh/ria.client.core/utils/mapper/flat-item';

import type { AllowedFeaturesData, LicenseData, LicenseListItem } from '~/common/model/api/license';
import type { CreateLicenseRequestData } from '~/services/server/api/license';

import axiosPlugin from '~/plugins/axios';

import type { GetAllowedFeaturesResponse, GetLicenseResponse, LicenseListResponse } from './_fetcher.type';

import requestConfig from './_request';

const { options, endpoints, createError } = requestConfig;

const client = axiosPlugin.createClient(options);

/** GetLicenseList fetcher. */
export const getLicenseListFetcher = createFetcher<QueryRequestData, DataSource<LicenseListItem>>(endpoints.getLicenseList.key, async (query) => {
  try {
    const { page, total, items } = getPageableData<LicenseListResponse>(query, await client.request(endpoints.getLicenseList(query)));

    return {
      items,
      total,
      page,
      sort: query.sort,
      filter: query.filter ?? undefined,
    };
  } catch (exception) {
    throw createError('getLicenseList', 'Failed to get license list.', exception);
  }
});

/** CreateLicense fetcher. */
export const createLicenseFetcher = createFetcher<CreateLicenseRequestData, LicenseListItem>(endpoints.createLicense.key, async (requestData) => {
  try {
    return getData<LicenseListItem>(await client.request(endpoints.createLicense(requestData)));
  } catch (exception) {
    throw createError('createLicense', 'Failed to create license.', exception);
  }
});

export const previewLicenseFetcher = createFetcher<string, LicenseData>(endpoints.getLicenses.key, async (id) => {
  try {
    const data = getData<GetLicenseResponse>(await client.request(endpoints.getLicenses(id)));

    return {
      ...data,
      singleSignOn: data.singleSignOn ? 'azureEntra' : undefined,
      analyticOptions: data.analyticOptions
        ? flatItemMapper.prepare(data.analyticOptions, {
            emptyItem: 'none',
          })
        : undefined,
      azureAiOptions: data.azureAiOptions
        ? flatItemMapper.prepare(data.azureAiOptions, {
            emptyItem: 'none',
          })
        : undefined,
      natifAiOptions: data.natifAiOptions
        ? flatItemMapper.prepare(data.natifAiOptions, {
            emptyItem: 'none',
          })
        : undefined,
    };
  } catch (exception) {
    throw createError('getLicenses', 'Failed to preview license.', exception);
  }
});

/** ValidateLicenseCode fetcher. */
export const validateLicenseCodeFetcher = createFetcher<string>(endpoints.validateLicenseCode.key, async (id) => {
  try {
    await client.request(endpoints.validateLicenseCode(id));
  } catch (exception) {
    throw createError('validateLicenseCode', 'Failed to validate license code.', exception);
  }
});

export const getAllowedFeaturesFetcher = createFetcher<void, AllowedFeaturesData>(endpoints.getAllowedFeatures.key, async () => {
  try {
    const data = getData<GetAllowedFeaturesResponse>(await client.request(endpoints.getAllowedFeatures()));

    return {
      ...data,
      aiProvider: data.aiProvider
        ? flatItemMapper.prepare(data.aiProvider, {
            emptyItem: 'none',
          })
        : [],
      packageLevel: data.packageLevel ?? data.level,
      singleSignOn: data.singleSignOn ? 'azureEntra' : undefined,
      analyticOptions: data.analyticOptions
        ? flatItemMapper.prepare(data.analyticOptions, {
            emptyItem: 'none',
          })
        : undefined,
      azureAiOptions: data.azureAiOptions
        ? flatItemMapper.prepare(data.azureAiOptions, {
            emptyItem: 'none',
          })
        : undefined,
      natifAiOptions: data.natifAiOptions
        ? flatItemMapper.prepare(data.natifAiOptions, {
            emptyItem: 'none',
          })
        : undefined,
      isEnabledWss: data.isEnabledWss ?? false,
      databaseOptions: data.databaseOptions
        ? flatItemMapper.prepare(data.databaseOptions, {
            emptyItem: 'none',
          })
        : [],
    };
  } catch (exception) {
    throw createError('getAllowedFeatures', 'Failed to get allowed features.', exception);
  }
});
